import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, Alert } from '@mui/material';
import { Add, Visibility, Delete, ArrowBack, Event, People, Laptop, LocationOn } from '@mui/icons-material';
import api from '../../services/api';

const formatLabels = {
  praesenz: { label: 'Präsenz', color: 'success' },
  online: { label: 'Online', color: 'info' },
  hybrid: { label: 'Hybrid', color: 'warning' },
};

const statusLabels = {
  geplant: { label: 'Geplant', color: 'default' },
  bestaetigt: { label: 'Bestätigt', color: 'info' },
  durchgefuehrt: { label: 'Durchgeführt', color: 'success' },
  abgesagt: { label: 'Abgesagt', color: 'error' },
};

const VeranstaltungenPage = () => {
  const { projektId } = useParams();
  const navigate = useNavigate();
  const [veranstaltungen, setVeranstaltungen] = useState([]);
  const [statistik, setStatistik] = useState(null);
  const [projektTitel, setProjektTitel] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    titel: '', datum: '', uhrzeitVon: '', uhrzeitBis: '', ort: '',
    format: 'praesenz', maxTeilnehmende: '', beschreibung: ''
  });

  const load = useCallback(async () => {
    try {
      const [vRes, sRes, pRes] = await Promise.all([
        api.get(`/veranstaltungen/projekt/${projektId}`),
        api.get(`/veranstaltungen/projekt/${projektId}/statistik`),
        api.get(`/projekte/${projektId}`)
      ]);
      setVeranstaltungen(vRes.data);
      setStatistik(sRes.data);
      setProjektTitel(pRes.data.titel);
    } catch (err) { setError('Fehler beim Laden'); }
  }, [projektId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.uhrzeitVon) delete data.uhrzeitVon;
      if (!data.uhrzeitBis) delete data.uhrzeitBis;
      if (!data.maxTeilnehmende) delete data.maxTeilnehmende;
      if (!data.beschreibung) delete data.beschreibung;
      await api.post(`/veranstaltungen/projekt/${projektId}`, data);
      setOpen(false);
      setForm({ titel: '', datum: '', uhrzeitVon: '', uhrzeitBis: '', ort: '', format: 'praesenz', maxTeilnehmende: '', beschreibung: '' });
      load();
    } catch (err) { setError('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Veranstaltung wirklich löschen?')) {
      try {
        await api.delete(`/veranstaltungen/${id}`);
        load();
      } catch (err) { setError('Fehler beim Löschen'); }
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/projekte/${projektId}`)} sx={{ color: '#1B5E20' }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold" color="#1B5E20">Veranstaltungen</Typography>
          <Typography variant="body2" color="text.secondary">{projektTitel}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}
          sx={{ bgcolor: '#1B5E20', '&:hover': { bgcolor: '#2E7D32' } }}>
          Neue Veranstaltung
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Statistik-Cards */}
      {statistik && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#E8F5E9' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Event sx={{ color: '#1B5E20', fontSize: 32 }} />
                <Typography variant="h4" fontWeight="bold" color="#1B5E20">{statistik.gesamtVeranstaltungen}</Typography>
                <Typography variant="caption" color="text.secondary">Veranstaltungen</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <People sx={{ color: '#1565C0', fontSize: 32 }} />
                <Typography variant="h4" fontWeight="bold">{statistik.gesamtTeilnehmende}</Typography>
                <Typography variant="caption" color="text.secondary">Teilnehmende</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <LocationOn sx={{ color: '#2E7D32', fontSize: 32 }} />
                <Typography variant="h4" fontWeight="bold">{statistik.nachFormat?.praesenz || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Präsenz</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Laptop sx={{ color: '#1565C0', fontSize: 32 }} />
                <Typography variant="h4" fontWeight="bold">{(statistik.nachFormat?.online || 0) + (statistik.nachFormat?.hybrid || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">Online/Hybrid</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabelle */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Titel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Ort</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Format</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Teilnehmer</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {veranstaltungen.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Veranstaltungen vorhanden</TableCell></TableRow>
            ) : veranstaltungen.map((v) => (
              <TableRow key={v.id} hover>
                <TableCell>{v.titel}</TableCell>
                <TableCell>{formatDate(v.datum)}</TableCell>
                <TableCell>{v.ort || '-'}</TableCell>
                <TableCell>
                  <Chip label={formatLabels[v.format]?.label || v.format}
                    color={formatLabels[v.format]?.color || 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={statusLabels[v.status]?.label || v.status}
                    color={statusLabels[v.status]?.color || 'default'} size="small" />
                </TableCell>
                <TableCell>
                  {v.teilnehmerAnzahl}{v.maxTeilnehmende ? `/${v.maxTeilnehmende}` : ''}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => navigate(`/veranstaltungen/${projektId}/${v.id}`)} sx={{ color: '#1B5E20' }}>
                    <Visibility />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(v.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neue Veranstaltung</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Titel *" value={form.titel}
            onChange={(e) => setForm({...form, titel: e.target.value})} margin="normal" />
          <TextField fullWidth label="Datum *" type="date" value={form.datum}
            onChange={(e) => setForm({...form, datum: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth label="Uhrzeit von" type="time" value={form.uhrzeitVon}
                onChange={(e) => setForm({...form, uhrzeitVon: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Uhrzeit bis" type="time" value={form.uhrzeitBis}
                onChange={(e) => setForm({...form, uhrzeitBis: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
          <TextField fullWidth label="Ort" value={form.ort}
            onChange={(e) => setForm({...form, ort: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Format" value={form.format}
            onChange={(e) => setForm({...form, format: e.target.value})} margin="normal">
            <MenuItem value="praesenz">Präsenz</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="hybrid">Hybrid</MenuItem>
          </TextField>
          <TextField fullWidth label="Max. Teilnehmende" type="number" value={form.maxTeilnehmende}
            onChange={(e) => setForm({...form, maxTeilnehmende: e.target.value})} margin="normal" />
          <TextField fullWidth label="Beschreibung" value={form.beschreibung}
            onChange={(e) => setForm({...form, beschreibung: e.target.value})} margin="normal" multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VeranstaltungenPage;
